/**
 * FilterPanel.tsx - Main Filter Panel Component
 *
 * Optimized to open instantly by deferring all heavy work.
 * Only loads filter content when accordion section is opened.
 */

import React, { useState, useEffect, useRef, memo } from 'react';
import {
    Box,
    HStack,
    Text,
    Button,
    IconButton,
    Portal,
    Spinner,
} from '@chakra-ui/react';
import {
    AccordionRoot,
    AccordionItem,
    AccordionItemTrigger,
    AccordionItemContent,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { LuX, LuChevronDown, LuChevronUp } from 'react-icons/lu';
import { useFilterContext } from '@/contexts/FilterContext';
import { useCircleContext } from '@/contexts/CircleContext';
import { DateRangeFilter } from './DateRangeFilter';
import { SearchableMultiSelect } from './SearchableMultiSelect';
import { DeweyTreeFilter } from './DeweyTreeFilter';
import { ActiveFilters, EMPTY_FILTERS, FilterOption } from '@/types/filter.types';
import { getTranslationKey } from '@/utilities/getTranslationKey';
import { HIDE_UNRESOLVED_CATEGORIES, isLabelResolved } from '@/utilities/deweyCodeParser';

interface FilterPanelProps {
    isOpen: boolean;
    onClose: () => void;
    anchorRef?: React.RefObject<HTMLElement>;
}

// Memoized filter components to prevent re-renders
const MemoizedMultiSelect = memo(SearchableMultiSelect);
const MemoizedTreeFilter = memo(DeweyTreeFilter);

// Loading placeholder
const FilterLoading = () => (
    <HStack justifyContent="center" padding={4}>
        <Spinner size="sm" />
        <Text fontSize="sm" color="gray.500">Loading...</Text>
    </HStack>
);

export const FilterPanel: React.FC<FilterPanelProps> = ({ isOpen, onClose, anchorRef }) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir() === 'rtl';
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

    const {
        activeFilters,
        setActiveFilters,
        filterSuggestions,
        loadingSuggestions,
        resetFilters,
        loadFilterSuggestions,
        updateDateRange,
        updateAuthors,
        updatePersons,
        updateOrganizations,
        updateLocations,
        updateCategoryCodes,
        findCategoryLabel,
    } = useFilterContext();

    const { parentCircleId } = useCircleContext();

    const [mobileLocalFilters, setMobileLocalFilters] = useState<ActiveFilters>(activeFilters);
    const [accordionValue, setAccordionValue] = useState<string[]>(['date-range']);
    const [loadedSections, setLoadedSections] = useState<Set<string>>(new Set(['date-range']));
    
    const prevIsOpenRef = useRef(false);

    // Load filter suggestions when panel opens
    useEffect(() => {
        if (isOpen && parentCircleId && !filterSuggestions && !loadingSuggestions) {
            loadFilterSuggestions(parentCircleId);
        }
    }, [isOpen, parentCircleId, filterSuggestions, loadingSuggestions, loadFilterSuggestions]);

    // Track which sections have been opened
    useEffect(() => {
        if (accordionValue.length > 0) {
            setLoadedSections((prev) => {
                const next = new Set(prev);
                accordionValue.forEach((v) => next.add(v));
                return next;
            });
        }
    }, [accordionValue]);

    // Mobile: sync local state when panel opens
    useEffect(() => {
        if (isMobile && isOpen && !prevIsOpenRef.current) {
            setMobileLocalFilters(activeFilters);
        }
        prevIsOpenRef.current = isOpen;
    }, [isOpen, isMobile, activeFilters]);

    // Reset loaded sections AND accordion state when panel closes
    useEffect(() => {
        if (!isOpen) {
            setLoadedSections(new Set(['date-range']));
            setAccordionValue(['date-range']);
        }
    }, [isOpen]);

    const displayFilters = isMobile ? mobileLocalFilters : activeFilters;

    // Map category codes - only if section is loaded
    const mappedCategoryCodes = React.useMemo(() => {
        if (!loadedSections.has('categories') || !filterSuggestions?.categoryCodes) {
            return [];
        }
        return filterSuggestions.categoryCodes.reduce<FilterOption[]>((acc, option) => {
            const rawLabel = findCategoryLabel(option.value);
            // Hide categories that resolve to a bare dewey number
            if (HIDE_UNRESOLVED_CATEGORIES && !isLabelResolved(option.value, rawLabel)) {
                return acc;
            }
            const translationKey = getTranslationKey(rawLabel);
            const translated = t(translationKey, { ns: 'topics' });
            acc.push({
                ...option,
                displayLabel: translated !== translationKey ? translated : rawLabel,
            });
            return acc;
        }, []);
    }, [loadedSections, filterSuggestions?.categoryCodes, findCategoryLabel, t, i18n.language]);

    // Handlers
    const handleDateRangeChange = (dateRange: ActiveFilters['dateRange']) => {
        if (isMobile) {
            setMobileLocalFilters((prev) => ({ ...prev, dateRange }));
        } else {
            updateDateRange(dateRange);
        }
    };

    const handleAuthorsChange = (authors: string[]) => {
        if (isMobile) {
            setMobileLocalFilters((prev) => ({ ...prev, authors }));
        } else {
            updateAuthors(authors);
        }
    };

    const handlePersonsChange = (persons: string[]) => {
        if (isMobile) {
            setMobileLocalFilters((prev) => ({ ...prev, persons }));
        } else {
            updatePersons(persons);
        }
    };

    const handleOrganizationsChange = (organizations: string[]) => {
        if (isMobile) {
            setMobileLocalFilters((prev) => ({ ...prev, organizations }));
        } else {
            updateOrganizations(organizations);
        }
    };

    const handleLocationsChange = (locations: string[]) => {
        if (isMobile) {
            setMobileLocalFilters((prev) => ({ ...prev, locations }));
        } else {
            updateLocations(locations);
        }
    };

    const handleCategoryCodesChange = (categoryCodes: string[]) => {
        if (isMobile) {
            setMobileLocalFilters((prev) => ({ ...prev, categoryCodes }));
        } else {
            updateCategoryCodes(categoryCodes);
        }
    };

    const handleApply = () => {
        setActiveFilters(mobileLocalFilters);
        onClose();
    };

    const handleCancel = () => {
        setMobileLocalFilters(activeFilters);
        onClose();
    };

    const handleResetAll = () => {
        if (isMobile) {
            setMobileLocalFilters(EMPTY_FILTERS);
        } else {
            resetFilters();
        }
    };

    const getSelectionCount = (category: string): number => {
        switch (category) {
            case 'date-range':
                return displayFilters.dateRange.customFrom || displayFilters.dateRange.customTo ? 1 : 0;
            case 'authors':
                return displayFilters.authors.length;
            case 'persons':
                return displayFilters.persons.length;
            case 'organizations':
                return displayFilters.organizations.length;
            case 'locations':
                return displayFilters.locations.length;
            case 'categories':
                return displayFilters.categoryCodes.length;
            default:
                return 0;
        }
    };

    const renderAccordionTrigger = (title: string, category: string) => {
        const count = getSelectionCount(category);
        const isExpanded = accordionValue.includes(category);
        return (
            <AccordionItemTrigger cursor="pointer">
                <HStack justifyContent="space-between" width="100%" flexDirection={isRTL ? 'row-reverse' : 'row'}>
                    <Text fontSize="sm" fontWeight="600" color="gray.700">
                        {title}
                        {count > 0 && (
                            <Text as="span" color="gray.500" fontWeight="400" marginLeft={isRTL ? 0 : 2} marginRight={isRTL ? 2 : 0}>
                                ({count} {t('filter_selected') || 'selected'})
                            </Text>
                        )}
                    </Text>
                    {isExpanded ? <LuChevronUp /> : <LuChevronDown />}
                </HStack>
            </AccordionItemTrigger>
        );
    };

    // Helper to render filter content only when section is loaded
    const renderFilterContent = (
        sectionKey: string,
        options: FilterOption[] | undefined,
        selectedValues: string[],
        onChangeHandler: (values: string[]) => void,
        placeholderKey: string,
        emptyMessageKey: string
    ) => {
        if (!loadedSections.has(sectionKey)) {
            return <FilterLoading />;
        }

        return (
            <MemoizedMultiSelect
                options={options || []}
                selectedValues={selectedValues}
                onChange={onChangeHandler}
                placeholder={t(placeholderKey) || 'Search...'}
                emptyMessage={t(emptyMessageKey) || 'No options available'}
                searchField={sectionKey}
                circleId={parentCircleId}
            />
        );
    };

    const renderAccordionContent = () => (
        <AccordionRoot
            collapsible
            value={accordionValue}
            onValueChange={(e) => setAccordionValue(e.value)}
        >
            {/* Date Range */}
            <AccordionItem value="date-range">
                {renderAccordionTrigger(t('filter_publication_date') || 'Publication Date', 'date-range')}
                <AccordionItemContent>
                    <DateRangeFilter value={displayFilters.dateRange} onChange={handleDateRangeChange} />
                </AccordionItemContent>
            </AccordionItem>

            {/* Authors */}
            <AccordionItem value="authors">
                {renderAccordionTrigger(t('filter_author') || 'Author', 'authors')}
                <AccordionItemContent>
                    {renderFilterContent(
                        'authors',
                        filterSuggestions?.authors,
                        displayFilters.authors,
                        handleAuthorsChange,
                        'filter_search_authors',
                        'filter_no_authors'
                    )}
                </AccordionItemContent>
            </AccordionItem>

            {/* Persons */}
            <AccordionItem value="persons">
                {renderAccordionTrigger(t('filter_person') || 'Person', 'persons')}
                <AccordionItemContent>
                    {renderFilterContent(
                        'persons',
                        filterSuggestions?.persons,
                        displayFilters.persons,
                        handlePersonsChange,
                        'filter_search_persons',
                        'filter_no_persons'
                    )}
                </AccordionItemContent>
            </AccordionItem>

            {/* Organizations */}
            <AccordionItem value="organizations">
                {renderAccordionTrigger(t('filter_organization') || 'Organisation', 'organizations')}
                <AccordionItemContent>
                    {renderFilterContent(
                        'organizations',
                        filterSuggestions?.organizations,
                        displayFilters.organizations,
                        handleOrganizationsChange,
                        'filter_search_organizations',
                        'filter_no_organizations'
                    )}
                </AccordionItemContent>
            </AccordionItem>

            {/* Locations */}
            <AccordionItem value="locations">
                {renderAccordionTrigger(t('filter_location') || 'Location', 'locations')}
                <AccordionItemContent>
                    {renderFilterContent(
                        'locations',
                        filterSuggestions?.locations,
                        displayFilters.locations,
                        handleLocationsChange,
                        'filter_search_locations',
                        'filter_no_locations'
                    )}
                </AccordionItemContent>
            </AccordionItem>

            {/* Categories */}
            <AccordionItem value="categories">
                {renderAccordionTrigger(t('filter_topics') || 'Topics', 'categories')}
                <AccordionItemContent>
                    {loadedSections.has('categories') ? (
                        <MemoizedTreeFilter
                            options={mappedCategoryCodes}
                            selectedValues={displayFilters.categoryCodes}
                            onChange={handleCategoryCodesChange}
                            maxHeight={300}
                        />
                    ) : (
                        <FilterLoading />
                    )}
                </AccordionItemContent>
            </AccordionItem>
        </AccordionRoot>
    );

    if (!isOpen) return null;

    // Desktop Dropdown
    if (!isMobile && anchorRef?.current) {
        const rect = anchorRef.current.getBoundingClientRect();
        const dropdownStyle = {
            position: 'fixed' as const,
            bottom: `${window.innerHeight - rect.top + 8}px`,
            [isRTL ? 'right' : 'left']: isRTL ? `${window.innerWidth - rect.right}px` : `${rect.left}px`,
            width: '420px',
            maxHeight: '70vh',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column' as const,
        };

        return (
            <>
                <Portal>
                    <Box position="fixed" top={0} left={0} right={0} bottom={0} zIndex={999} onClick={onClose} />
                </Portal>
                <Portal>
                    <Box style={dropdownStyle} dir={isRTL ? 'rtl' : 'ltr'}>
                        <HStack justifyContent="space-between" paddingX={4} paddingY={3} borderBottom="1px solid" borderColor="gray.200">
                            <Text fontSize="md" fontWeight="700" color="gray.800">{t('filters') || 'Filters'}</Text>
                            <Button variant="ghost" size="sm" onClick={handleResetAll} color="gray.600">
                                {t('filter_reset_all') || 'Reset All'}
                            </Button>
                        </HStack>
                        <Box flex="1" overflowY="auto" paddingX={4} paddingY={3}>
                            {loadingSuggestions ? <FilterLoading /> : renderAccordionContent()}
                        </Box>
                    </Box>
                </Portal>
            </>
        );
    }

    // Mobile Modal
    return (
        <Portal>
            <Box position="fixed" top={0} left={0} right={0} bottom={0} background="rgba(0, 0, 0, 0.5)" zIndex={1000} onClick={onClose} />
            <Box
                position="fixed"
                bottom={0}
                left={0}
                right={0}
                maxHeight="90vh"
                background="white"
                borderTopLeftRadius="16px"
                borderTopRightRadius="16px"
                zIndex={1001}
                display="flex"
                flexDirection="column"
                dir={isRTL ? 'rtl' : 'ltr'}
            >
                <HStack justifyContent="space-between" paddingX={4} paddingY={3} borderBottom="1px solid" borderColor="gray.200">
                    <IconButton aria-label="Close" variant="ghost" size="sm" onClick={onClose}>
                        <LuX />
                    </IconButton>
                    <Text fontSize="md" fontWeight="700" color="gray.800">{t('filters') || 'Filters'}</Text>
                    <Box width="32px" />
                </HStack>
                <Box flex="1" overflowY="auto" paddingX={4} paddingY={3}>
                    {loadingSuggestions ? <FilterLoading /> : renderAccordionContent()}
                </Box>
                <HStack paddingX={4} paddingY={3} borderTop="1px solid" borderColor="gray.200" gap={2} dir={isRTL ? 'rtl' : 'ltr'}>
                    <Button variant="outline" size="sm" onClick={handleResetAll} flex="1">
                        {t('filter_reset_all') || 'Reset All'}
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleCancel} flex="1">
                        {t('cancel') || 'Cancel'}
                    </Button>
                    <Button variant="solid" size="sm" onClick={handleApply} flex="1" background="#333333" color="white" _hover={{ background: '#444444' }}>
                        {t('filter_apply') || 'Apply'}
                    </Button>
                </HStack>
            </Box>
        </Portal>
    );
};